import type { ReactNode } from "react";

interface TwoColumnLayoutProperties {
  /** Primary column content (wider share, rendered first). */
  firstColumn: ReactNode;
  /** Secondary column content (narrower share, rendered second). */
  secondColumn: ReactNode;
}

/**
 * Two-column page layout: single column (stacked, full width) below `sm`; a
 * 16-col grid from `sm` on, split 10/6 (62.5/37.5) through just-below-`xl`
 * and 12/4 (75/25) from `xl` on — one grid throughout, only the spans change.
 * Each column scrolls independently once split; below `sm`, the whole layout
 * scrolls as one.
 *
 * Tailwind classes must stay as static literals (not interpolated) for the
 * JIT compiler to pick them up — to change a ratio or breakpoint, edit the
 * `col-span-*` classes below directly. 16 columns needs an arbitrary
 * `grid-cols-[repeat(16,...)]` value since Tailwind's default scale stops at
 * 12; `col-span-*` are standard utilities and apply against however many
 * columns the grid defines.
 */
function TwoColumnLayout({ firstColumn, secondColumn }: TwoColumnLayoutProperties) {
  return (
    <div className="grid min-h-0 flex-1 grid-cols-1 gap-2 overflow-y-auto p-2 sm:grid-cols-[repeat(16,minmax(0,1fr))] sm:overflow-hidden">
      <main className="flex min-h-0 flex-col sm:col-span-10 sm:overflow-hidden xl:col-span-12">{firstColumn}</main>
      {/* pb reads --fab-clearance (see theme.css) — the single source of
          truth shared with ActionFabCluster's own `bottom` offset, so this
          column's height reliably stops above the fixed FAB cluster instead
          of the scrollable panel rendering underneath it. */}
      <aside className="flex min-h-0 flex-col gap-2 pb-[var(--fab-clearance)] sm:col-span-6 sm:h-full xl:col-span-4">
        {secondColumn}
      </aside>
    </div>
  );
}

export default TwoColumnLayout;
